import React, { useEffect, useState } from 'react';
import { Modal, Radio, Input, Space, Typography, message } from 'antd';
import moment from 'moment-timezone';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import { useAuth } from '../../context/auth.context';
import { DateAndTimeFormat } from '../../config/config';

const POLL_MS = 5 * 60 * 1000;

const CallOutcomePopup = () => {
    const { apiClient, checkUserAccessToResource } = useAuth();
    const canRead = !!checkUserAccessToResource({ resourceName: 'request_callback', action: 'read' });
    const location = useLocation();
    const history = useHistory();
    const outcomeId = qs.parse(location.search, { ignoreQueryPrefix: true }).outcome;
    const [pending, setPending] = useState([]);
    const [callHappened, setCallHappened] = useState(null);
    const [notes, setNotes] = useState('');
    const [closeCallback, setCloseCallback] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snoozedUntil, setSnoozedUntil] = useState(0);

    useEffect(() => {
        if (!canRead) return;
        const fetchPending = async (initial = false) => {
            try {
                const res = await apiClient.get(
                    `/api/request-callback/pending-outcome${outcomeId ? `?id=${outcomeId}` : ''}`,
                );
                let rows = res.data?.data || [];
                if (outcomeId) {
                    // opened via "Submit call outcome": show only that callback, never a different one
                    const target = rows[0];
                    if (initial) {
                        if (target) setSnoozedUntil(0);
                        else message.warning('This callback is not assigned to you or its outcome is already submitted.');
                    }
                }
                setPending(rows);
            } catch (error) {
                // best-effort; next poll retries
            }
        };
        fetchPending(true);
        const timer = setInterval(() => fetchPending(false), POLL_MS);
        return () => clearInterval(timer);
    }, [canRead, outcomeId, location.key]); // location.key: re-run when same ?outcome= is pushed again

    const current = pending[0];
    if (!current) return null;

    const alreadyClosed = current.status === 'closed';

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const res = await apiClient.post('/api/request-callback/outcome', {
                requestCallbackId: current.id,
                callHappened,
                notes: notes.trim(),
            });
            if (closeCallback && !alreadyClosed) {
                await apiClient.post('/api/request-callback/update', {
                    requestCallbackId: current.id,
                    status: 'closed',
                });
            }
            const movedToStage = res.data?.data?.[0]?.movedToStage;
            message.success(
                movedToStage
                    ? `Saved. Contact moved to ${movedToStage.charAt(0).toUpperCase() + movedToStage.slice(1)}.`
                    : 'Call outcome saved.',
            );
            setCallHappened(null);
            setNotes('');
            setCloseCallback(null);
            setPending((prev) => prev.slice(1));
        } catch (error) {
            message.error('Could not save call outcome: ' + (error.response?.data?.message || ''));
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit =
        callHappened !== null && (!callHappened || notes.trim()) && (alreadyClosed || closeCallback !== null);

    return (
        <Modal
            open={Date.now() > snoozedUntil}
            title="How did the call go?"
            okText="Submit"
            cancelText="Later"
            confirmLoading={submitting}
            okButtonProps={{ disabled: !canSubmit }}
            onOk={handleSubmit}
            onCancel={() => setSnoozedUntil(Date.now() + POLL_MS)}
            maskClosable={false}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text strong>
                    {current.contactName || current.contactPhone || current.contactEmail}
                </Typography.Text>
                <Typography.Text type="secondary">
                    {moment(current.reachOutDateTime).format(DateAndTimeFormat)} · {current.reachOutPlatform}
                </Typography.Text>
                {current.requirement && <Typography.Text>{current.requirement}</Typography.Text>}
                <Typography.Text strong>Call outcome</Typography.Text>
                <Radio.Group value={callHappened} onChange={(e) => setCallHappened(e.target.value)}>
                    <Radio value={true}>Connected</Radio>
                    <Radio value={false}>Not connected</Radio>
                </Radio.Group>
                <Typography.Text strong>
                    {callHappened === false
                        ? 'Reason (optional)'
                        : 'What was discussed? Any commitments or next steps?'}
                </Typography.Text>
                <Input.TextArea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={
                        callHappened === false
                            ? 'Why did the call not connect?'
                            : 'Add a brief summary of the discussion, including any commitments or next steps.'
                    }
                />
                {!alreadyClosed && (
                    <>
                        <Typography.Text strong>Needs follow-up?</Typography.Text>
                        {/* Yes = keep callback open, No = close it */}
                        <Radio.Group
                            value={closeCallback === null ? null : !closeCallback}
                            onChange={(e) => setCloseCallback(!e.target.value)}
                        >
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </>
                )}
            </Space>
        </Modal>
    );
};

export default CallOutcomePopup;
